import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { moduleKillSwitchService } from "@/services/module-kill-switch-service";
import {
  KillSwitchModule,
  KillSwitchState,
  ModuleAuditQuery,
  ModuleAuditResponse,
  ToggleModuleRequest,
} from "@/types/module-kill-switch";

export const killSwitchKeys = {
  all: ["module-kill-switches"] as const,
  states: () => [...killSwitchKeys.all, "states"] as const,
  audit: (query: ModuleAuditQuery) =>
    [...killSwitchKeys.all, "audit", query] as const,
};

export const useKillSwitchStates = () => {
  return useQuery<KillSwitchState[]>({
    queryKey: killSwitchKeys.states(),
    queryFn: () => moduleKillSwitchService.getStates(),
    staleTime: 15_000,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
};

export const useKillSwitchAudit = (query: ModuleAuditQuery) => {
  return useQuery<ModuleAuditResponse>({
    queryKey: killSwitchKeys.audit(query),
    queryFn: () => moduleKillSwitchService.getAudit(query),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
};

interface ToggleVariables {
  module: KillSwitchModule;
  payload: ToggleModuleRequest;
}

export const useToggleKillSwitch = () => {
  const queryClient = useQueryClient();

  return useMutation<KillSwitchState, unknown, ToggleVariables>({
    mutationFn: ({ module, payload }) =>
      moduleKillSwitchService.toggle(module, payload, { disableToast: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: killSwitchKeys.all });
    },
  });
};
